// @flow
import React, {Fragment} from 'react';
import classNames from 'classnames';
import {filter as filterGraphQlAnywhere} from 'graphql-anywhere';
import Price, {fragments as priceFragments} from '../Price/Price';
import Rating, {fragments as ratingFragments} from '../Rating/Rating';
import Details from '../Details/Details';
import GeoDistance from '../GeoDistance/GeoDistance';
import HitLabel from '../HitLabel/HitLabel';
import TaxesAndFeesPaidAtProperty from './TaxesAndFeesPaidAtProperty';
import BadgeTooltip from './ReviewBadgeTooltip';
import PriceSummary from '@homeaway/react-price-summary';

import type {Props as HitInfoProps} from './HitInfo';
import type {Badge} from './HitInfo';

type Props = {
    premierPartnerBadge: ?Badge,
    rankedBadges: Badge[]
} & HitInfoProps;

const Superlative = ({reviewBadges}) => {
    if (!reviewBadges[0].helpText) {
        return (
            <span className="HitInfo__superlative">{reviewBadges[0].name}</span>
        );
    }

    return (
        <span>
            <span className="HitInfo__superlative">{reviewBadges[0].name}</span>
            <BadgeTooltip helpText={reviewBadges[0].helpText} />
        </span>
    );
};

// TODO: move minStay logic to lingua
// https://jira.homeawaycorp.com/browse/SERP-2848
export default function HitInfoMobile({
    brand,
    rankedBadges,
    intl,
    isDatelessSearch,
    isMetric,
    listing,
    premierPartnerBadge,
    fullLocale,
    showIncludesTaxesAndFeesMessage,
    vrboOpasSecondaryPriceIsOn
}: Props) {
    const {instantBookable, reviewBadges, reviewCount, geoDistance} = listing;

    // prevents undesired content shift if there is no reviewBadge
    let priceDetailsClass =
        reviewBadges && reviewBadges[0] && reviewBadges[0].helpText
            ? 'HitInfo__priceSuperlativeGroup--extended'
            : 'HitInfo__priceSuperlativeGroup';

    return (
        <Fragment>
            <div className="media-flex__body">
                <div className={priceDetailsClass}>
                    <div
                        className={classNames('HitInfo__price', {
                            'HitInfo__Price--condensed': vrboOpasSecondaryPriceIsOn
                        })}
                    >
                        <Price
                            listing={filterGraphQlAnywhere(
                                priceFragments.listing,
                                listing
                            )}
                            useAverageLabel={isDatelessSearch}
                            fullLocale={fullLocale}
                            instantBookable={instantBookable}
                            intl={intl}
                        />
                        {vrboOpasSecondaryPriceIsOn &&
                            listing.priceSummarySecondary && (
                                <span className="PriceSummary__secondary">
                                    <PriceSummary
                                        priceSummary={
                                            listing.priceSummarySecondary
                                        }
                                    />
                                </span>
                            )}
                    </div>
                    {!!reviewBadges &&
                        !!reviewBadges.length && (
                            <Superlative reviewBadges={reviewBadges} />
                        )}
                </div>
                {/* UK-only regulatory requirement: see mapStateToProps in HitInfo.js */}
                {showIncludesTaxesAndFeesMessage && (
                    <div
                        className="HitInfo__taxesAndFeesPaidAtProperty text-muted micro"
                        style={{marginBottom: 8}}
                    >
                        <TaxesAndFeesPaidAtProperty />
                    </div>
                )}

                <div className="HitInfo__details">
                    <Details listing={listing} isMetric={isMetric} />
                </div>
                {geoDistance && (
                    <GeoDistance geoDistance={geoDistance} brand={brand} />
                )}
                <div className="HitInfo__badgeRatingGroup">
                    {reviewCount > 0 && (
                        <div className="HitInfo__rating">
                            <Rating
                                listing={filterGraphQlAnywhere(
                                    ratingFragments.listing,
                                    listing
                                )}
                            />
                        </div>
                    )}
                    {premierPartnerBadge && (
                        <div
                            className="small"
                            data-wdio="premier-partner-badge"
                            aria-hidden="true"
                        >
                            {premierPartnerBadge.name}
                        </div>
                    )}
                </div>
                {rankedBadges.length > 0 && (
                    <div className="HitInfo__labels">
                        {rankedBadges.map((badge, index) => (
                            <HitLabel
                                key={index}
                                id={badge.id}
                                label={badge.name}
                            />
                        ))}
                    </div>
                )}
            </div>
        </Fragment>
    );
}
